import { Card, Table, Button, Space, Empty, Input, Modal, Tag, message, Popconfirm } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, CloseCircleOutlined, CameraOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useState, useEffect } from "react";
import type { ControlPoint } from "../../../types";
import { controlPointService } from "../../../services/api";
import { PhotoGalleryModal } from "../../../components/PhotoGalleryModal";
import { ControlPointReviewModal } from "./ControlPointReviewModal";
import dayjs from "dayjs";

interface ControlPointsSectionProps {
  auditId: number;
  farmId: number;
  onRefresh?: () => void;
  /** Fires with the row count whenever the data loads — used by the
   *  parent to render a tab badge. */
  onCountChange?: (count: number) => void;
  /** When true, hides/disables every write action (create, edit,
   *  delete, sample add). Used when the parent audit is finalised
   *  — the backend rejects writes anyway, this just keeps the UI
   *  honest. */
  readOnly?: boolean;
}

/**
 * ControlPointsSection Component
 * Displays control points for a specific audit/farm
 */
export const ControlPointsSection = ({
  auditId,
  farmId,
  onRefresh,
  onCountChange,
  readOnly = false,
}: ControlPointsSectionProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState<ControlPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [photoGalleryVisible, setPhotoGalleryVisible] = useState(false);
  const [selectedControlPoint, setSelectedControlPoint] = useState<{ id: string; name: string } | null>(null);
  const [editableControlPointIds, setEditableControlPointIds] = useState<string[]>([]);
  // Review modal — same UX as the adjacent-crop review: shows the
  // CP info, lets the auditor save a new review or close the CP
  // outright if it's been resolved on the ground.
  const [reviewVisible, setReviewVisible] = useState(false);
  const [cpForReview, setCpForReview] = useState<ControlPoint | null>(null);
  // Row-level "Dar de baja" — independent of the review wizard.
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [cpForClose, setCpForClose] = useState<ControlPoint | null>(null);
  const [closeReason, setCloseReason] = useState("");
  const [closing, setClosing] = useState(false);
  // Photo modal — opens the gallery with upload capability.
  const [cpForPhotos, setCpForPhotos] = useState<ControlPoint | null>(null);

  const resolveControlPointReviewId = async (
    cp: ControlPoint,
  ): Promise<string> => {
    // Reuse the review tied to this audit if it exists; otherwise
    // create a minimal one so the photo has a parent FK.
    const detail = await controlPointService.getControlPoint(cp.id);
    const existing = (detail.reviews || []).find(
      (r) => r.audit === auditId,
    );
    if (existing) return existing.id;
    const created = await controlPointService.createReview({
      audit_id: auditId,
      control_point_id: cp.id,
      comment: "Revisión auto-creada para adjuntar fotos.",
      estimated_surface: cp.total_estimated_surface ?? 0,
    });
    return (created as any).id ?? (created as any).review_id;
  };

  const handleConfirmCloseCp = async () => {
    if (!cpForClose) return;
    const reason = closeReason.trim();
    if (!reason) {
      message.warning("Indica el motivo del cierre");
      return;
    }
    setClosing(true);
    try {
      await controlPointService.closeControlPoint(cpForClose.id, {
        closed_date: dayjs().format("YYYY-MM-DD"),
        comment_closed: reason,
        audit_id: auditId,
      } as any);
      message.success("Punto de control dado de baja");
      setCloseModalOpen(false);
      setCpForClose(null);
      setCloseReason("");
      loadData();
      onRefresh?.();
    } catch (error: any) {
      message.error(
        error?.message || "No se pudo dar de baja el punto de control",
      );
    } finally {
      setClosing(false);
    }
  };

  useEffect(() => {
    if (farmId && auditId) {
      loadData();
    }
  }, [farmId, auditId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const result = await controlPointService.getOpenControlPointsForFarm(farmId, auditId);
      
      // Handle GeoJSON FeatureCollection format
      let controlPoints = result.control_points || [];
      if (controlPoints && typeof controlPoints === 'object' && 'features' in controlPoints) {
        // It's a FeatureCollection, extract features
        controlPoints = (controlPoints as any).features || [];
      }
      
      const safe = Array.isArray(controlPoints) ? controlPoints : [];
      setData(safe);
      onCountChange?.(safe.length);

      // Store editable control point IDs
      setEditableControlPointIds(result.editable_control_point_ids || []);
    } catch (error: any) {
      message.error("Error al cargar puntos de control");
      setData([]);
      setEditableControlPointIds([]);
      onCountChange?.(0);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    navigate(`/control-points/create?auditId=${auditId}`);
  };

  const handleView = (id: string) => {
    navigate(`/control-points/${id}`, {
      state: { from: `/audits/${auditId}` }
    });
  };

  const handleEdit = (id: string) => {
    const isEditable = editableControlPointIds.includes(id);
    
    if (isEditable) {
      // For editable control points (created in this audit), use the full edit form
      navigate(`/control-points/edit-full/${id}?auditId=${auditId}`, {
        state: { from: `/audits/${auditId}` }
      });
    } else {
      // For non-editable control points, go to detail page to edit review
      navigate(`/control-points/${id}`, {
        state: { from: `/audits/${auditId}` }
      });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await controlPointService.deleteControlPoint(id);
      message.success("Punto de control eliminado");
      loadData();
      if (onRefresh) onRefresh();
    } catch (error: any) {
      message.error("Error al eliminar");
    }
  };

  const handleViewPhotos = (id: string, name: string) => {
    setSelectedControlPoint({ id, name });
    setPhotoGalleryVisible(true);
  };

  const handlePhotoDeleted = () => {
    loadData();
    if (onRefresh) onRefresh();
  };

  const columns = [
    {
      title: "Nombre",
      dataIndex: ["properties", "name"],
      key: "name",
      width: 130,
      ellipsis: true,
    },
    {
      title: "Estado",
      dataIndex: ["properties", "status"],
      key: "status",
      width: 90,
      render: (value: string) => (
        <Tag color={value === "open" ? "green" : "red"}>
          {value === "open" ? "Abierto" : "Cerrado"}
        </Tag>
      ),
    },
    {
      title: "Detección",
      dataIndex: ["properties", "detection_date"],
      key: "detection_date",
      width: 105,
      render: (value: string) => value ? dayjs(value).format("DD/MM/YYYY") : '-',
    },
    {
      title: "Sup. (ha)",
      dataIndex: ["properties", "total_estimated_surface"],
      key: "total_estimated_surface",
      width: 90,
      align: "center" as const,
      render: (value: number) => value ? value.toFixed(2) : '-',
    },
    {
      title: "Revisiones",
      dataIndex: ["properties", "review_count"],
      key: "review_count",
      width: 95,
      align: "center" as const,
    },
    {
      title: "Muestras",
      dataIndex: ["properties", "sample_count"],
      key: "sample_count",
      width: 90,
      align: "center" as const,
    },
    {
      title: "Acciones",
      key: "actions",
      width: 170,
      fixed: "right" as const,
      render: (_: any, record: any) => {
        const id = record.id || record.properties?.id;
        const isEditable = editableControlPointIds.includes(id);
        const cp: ControlPoint =
          (record as any).properties
            ? ({ id, ...(record as any).properties } as ControlPoint)
            : (record as ControlPoint);
        const isClosed = cp.status === "closed";
        return (
          <Space size="small">
            {isEditable ? (
              <>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(id)}
                  disabled={readOnly || isClosed}
                  title="Editar punto de control"
                />
                <Popconfirm
                  title="¿Eliminar este punto de control?"
                  onConfirm={() => handleDelete(id)}
                  okText="Sí"
                  cancelText="No"
                  disabled={readOnly}
                >
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    disabled={readOnly}
                    title="Eliminar"
                  />
                </Popconfirm>
              </>
            ) : (
              <Button
                size="small"
                type="primary"
                ghost
                onClick={() => {
                  setCpForReview(cp);
                  setReviewVisible(true);
                }}
                disabled={readOnly || isClosed}
                title="Revisión (datos + mapa)"
              >
                Revisión
              </Button>
            )}
            {/* Row-level "Fotos" — gallery + upload. Auto-creates
                a placeholder review on first upload if none exists
                for this audit. */}
            <Button
              size="small"
              icon={<CameraOutlined />}
              onClick={() => {
                setSelectedControlPoint({ id, name: cp.name });
                setCpForPhotos(cp);
                setPhotoGalleryVisible(true);
              }}
              disabled={isClosed}
              title="Fotos"
            />
            {/* Row-level "Dar de baja" — independent of the review
                wizard. Disabled once the CP has a review in this
                audit; revisar y luego dar de baja en el mismo paso
                no tiene sentido (la revisión ya implica un veredicto). */}
            <Button
              size="small"
              danger
              icon={<CloseCircleOutlined />}
              onClick={() => {
                setCpForClose(cp);
                setCloseReason("");
                setCloseModalOpen(true);
              }}
              disabled={readOnly || isClosed || !!cp.has_review_in_audit}
              title={
                cp.has_review_in_audit
                  ? "Este punto de control ya está revisado en esta auditoría"
                  : "Dar de baja"
              }
            />
          </Space>
        );
      },
    },
  ];

  if (!farmId || !auditId) {
    return null;
  }

  return (
    <Card
      title="Puntos de Control"
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
          disabled={readOnly}
        >
          Crear
        </Button>
      }
      style={{ marginBottom: 24 }}
      loading={loading}
    >
      {/* Row highlights: red for CPs just given de baja in this
          audit, green when the auditor has a review in this audit. */}
      <style>{`
        .control-points-table .cp-closed-here-row td {
          background-color: #fff1f0 !important;
        }
        .control-points-table .cp-reviewed-row td {
          background-color: #f6ffed !important;
        }
      `}</style>
      <Table
        className="control-points-table planning-audits-table"
        size="small"
        dataSource={data}
        columns={columns}
        rowKey={(record) => record.id || record.properties?.id || Math.random().toString()}
        pagination={false}
        scroll={{ x: 785 }}
        rowClassName={(record) => {
          const props = (record as any).properties || (record as any);
          if (
            props.status === "closed" &&
            props.closed_in_audit != null &&
            props.closed_in_audit === auditId
          ) {
            return "cp-closed-here-row";
          }
          if (props.has_review_in_audit) {
            return "cp-reviewed-row";
          }
          return "";
        }}
        locale={{
          emptyText: "No hay puntos de control registrados para esta finca"
        }}
      />

      {/* Photo Gallery Modal */}
      {selectedControlPoint && (
        <PhotoGalleryModal
          visible={photoGalleryVisible}
          onClose={() => {
            setPhotoGalleryVisible(false);
            setSelectedControlPoint(null);
            setCpForPhotos(null);
          }}
          entityId={selectedControlPoint.id}
          entityName={selectedControlPoint.name}
          entityType="controlPoint"
          onPhotoDeleted={handlePhotoDeleted}
          onPhotoUploaded={() => {
            loadData();
            onRefresh?.();
          }}
          upload={
            readOnly || !cpForPhotos
              ? undefined
              : {
                  auditId,
                  getTargetId: async () => {
                    if (!cpForPhotos) {
                      throw new Error("Punto de control no disponible.");
                    }
                    return resolveControlPointReviewId(cpForPhotos);
                  },
                }
          }
        />
      )}

      <ControlPointReviewModal
        visible={reviewVisible}
        onClose={() => {
          setReviewVisible(false);
          setCpForReview(null);
        }}
        onSuccess={() => {
          setReviewVisible(false);
          setCpForReview(null);
          // Reload list + propagate to the parent so other panels
          // (samples / history) pick up the new review or status.
          loadData();
          onRefresh?.();
        }}
        auditId={auditId}
        controlPoint={cpForReview}
      />

      <Modal
        title="Dar de baja el punto de control"
        open={closeModalOpen}
        onCancel={() => (closing ? undefined : setCloseModalOpen(false))}
        onOk={handleConfirmCloseCp}
        confirmLoading={closing}
        okText="Dar de baja"
        cancelText="Cancelar"
        okButtonProps={{ danger: true }}
        destroyOnClose
      >
        <p>
          Marca <strong>{cpForClose?.name}</strong> como cerrado en el
          catálogo. Indica brevemente el motivo (regularizado, eliminado,
          autorizado…).
        </p>
        <Input.TextArea
          rows={3}
          value={closeReason}
          onChange={(e) => setCloseReason(e.target.value)}
          placeholder="Motivo del cierre…"
          maxLength={500}
          showCount
        />
      </Modal>
    </Card>
  );
};
